import { yupResolver } from '@hookform/resolvers/yup';
import { AllergyItemMasterAddPayload } from 'models';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LiaTimesSolid } from 'react-icons/lia';
import { toast } from 'react-toastify';
import { CHART_COL_WIDTH_SOURCE, PDF_FORMAT_SOURCE } from 'utils';
import * as yup from 'yup';
import useAllergyItemMaster from '../hooks/useAllergyItemMaster';

interface AllergyItemMasterFormProps {
  nextDisplayOrder: number;
  onClose: () => void;
  onReflect?: () => void;
}

function AllergyItemMasterForm({ nextDisplayOrder, onClose, onReflect }: AllergyItemMasterFormProps) {
  const { t } = useTranslation();

  const { addAllergyItemMaster } = useAllergyItemMaster();

  // schema validation
  const schema = yup
    .object({
      allergy_item_name: yup.string().required(t('AllergyItemMaster.warn_field_required')),
      description: yup.string().default(''),
      priority: yup
        .number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() === '') {
            return undefined;
          }
          return value;
        })
        .min(1, t('AllergyItemMaster.warn_number_required'))
        .integer(t('AllergyItemMaster.warn_number_required'))
        .required(t('AllergyItemMaster.warn_number_required')),
      display_order: yup
        .number()
        .transform((value, originalValue) => {
          if (typeof originalValue === 'string' && originalValue.trim() === '') {
            return undefined;
          }
          return value;
        })
        .min(1, t('AllergyItemMaster.warn_number_required'))
        .integer(t('AllergyItemMaster.warn_number_required'))
        .required(t('AllergyItemMaster.warn_number_required')),
      pdf_format: yup.number().required(),
      chart_col_width: yup.number().required(),
    })
    .required();

  const {
    register,
    handleSubmit,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<AllergyItemMasterAddPayload>({
    resolver: yupResolver(schema),
  });
  const onSubmit = async (payload: AllergyItemMasterAddPayload) => {
    const { data, error } = await addAllergyItemMaster(payload);
    if (data) {
      toast.success(data?.message);
      onReflect?.();
      onClose();
    } else {
      if (error && error.error) {
        const values = getValues();
        const fieldNames = Object.keys(values);
        toast.error(<div dangerouslySetInnerHTML={{ __html: error.message || t('App.unexpected_error') }}></div>);
        for (const key of Object.keys(error.error)) {
          if (fieldNames.includes(key)) {
            setError(key as keyof AllergyItemMasterAddPayload, { type: 'custom', message: error.error[key] });
          }
        }
      }
    }
  };

  return (
    <Modal
      show
      size="lg"
      dialogClassName="allergy-item-master-form-modal"
      backdrop="static"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      onHide={onClose}
    >
      <Modal.Header className="border-bottom-0 justify-content-end">
        <Button variant="primary" className="btn--auto" onClick={onClose}>
          <LiaTimesSolid size={16} />
        </Button>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Form.Group as={Row} className="mb-3" controlId="allergy_item_name">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.allergy_item_name')}
            </Form.Label>
            <Col sm="8">
              <Form.Control
                {...register('allergy_item_name')}
                type="text"
                placeholder=""
                isInvalid={!!errors.allergy_item_name}
                autoFocus
              />
              <Form.Control.Feedback type="invalid">{errors.allergy_item_name?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="description">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.description')}
            </Form.Label>
            <Col sm="8">
              <Form.Control {...register('description')} type="text" placeholder="" isInvalid={!!errors.description} />
              <Form.Control.Feedback type="invalid">{errors.description?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="priority">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.priority')}
            </Form.Label>
            <Col sm="8">
              <Form.Control
                {...register('priority')}
                className="text-end"
                style={{ width: '80px' }}
                type="number"
                placeholder=""
                defaultValue={1}
                min={1}
                isInvalid={!!errors.priority}
              />
              <Form.Control.Feedback type="invalid">{errors.priority?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="display_order">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.display_order')}
            </Form.Label>
            <Col sm="8">
              <Form.Control
                {...register('display_order')}
                className="text-end"
                style={{ width: '80px' }}
                type="number"
                placeholder=""
                defaultValue={nextDisplayOrder}
                min={1}
                isInvalid={!!errors.display_order}
              />
              <Form.Control.Feedback type="invalid">{errors.display_order?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="pdf_format">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.pdf_format')}
            </Form.Label>
            <Col sm="8">
              <Form.Select {...register('pdf_format')} style={{ width: '80px' }}>
                {PDF_FORMAT_SOURCE.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.pdf_format?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <Form.Group as={Row} className="mb-3" controlId="chart_col_width">
            <Form.Label column sm="4" className="text-end">
              {t('AllergyItemMaster.chart_col_width')}
            </Form.Label>
            <Col sm="8">
              <Form.Select {...register('chart_col_width')} style={{ width: '80px' }}>
                {CHART_COL_WIDTH_SOURCE.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.chart_col_width?.message}</Form.Control.Feedback>
            </Col>
          </Form.Group>
          <div className="text-center mt-5">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="d-inline-flex gap-1 justify-content-center btn btn-primary align-items-center"
            >
              <span>{t('AllergyItemMaster.add')}</span>
              {isSubmitting && <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default AllergyItemMasterForm;
